import { supabase } from '../supabase';
import { sortDivisions } from '../utils/divisionDisplay';

// Phase C.2 — public-safe reads only. Every function here calls one of
// migration 038's SECURITY DEFINER RPCs, each already scoped to return
// an explicit, hand-picked public column list (never `select *`) and
// granted to BOTH anon and authenticated — these are the only RPCs in
// this codebase callable while signed out. No function in this file
// ever requires or checks a session; none of them can mutate anything.

/** The /programs public directory — every Program with status='active'
 * and a slug, each with its resolved current-Season year/status. */
export async function listPublicPrograms() {
  const { data, error } = await supabase.rpc('list_public_programs');
  if (error) throw error;
  return data;
}

/** Resolves a Program by slug. Returns null (never throws) for an
 * unknown/non-public slug — callers render their own not-found state. */
export async function getPublicProgram(slug) {
  const { data, error } = await supabase.rpc('get_public_program', { p_slug: slug });
  if (error) throw error;
  return data?.[0] ?? null;
}

/** The Season a Program page should display. Omit `year` for the
 * deterministic auto-resolution (active -> soonest upcoming -> most
 * recent archived -> none); pass a specific year for an explicit
 * historical link. Returns null for "no Season to show." */
export async function getPublicProgramSeason(programId, year = null) {
  const { data, error } = await supabase.rpc('get_public_program_season', {
    p_program_id: programId,
    p_year: year,
  });
  if (error) throw error;
  return data?.[0] ?? null;
}

/** Every Season for a Program, newest first — powers the minimal "other
 * seasons" link list (only worth rendering when there's more than one). */
export async function listPublicProgramSeasons(programId) {
  const { data, error } = await supabase.rpc('list_public_program_seasons', { p_program_id: programId });
  if (error) throw error;
  return data;
}

/** Every active Division for a Season, in canonical grade order (Phase
 * C.3.1 — see utils/divisionDisplay.js's own sortDivisions()/
 * compareDivisions() header for why: the RPC itself still returns
 * display_order/name order (038 is not edited by this cleanup), which
 * for a migration-040-backfilled Program is an artifact of alphabetical
 * Team-name encounter order, not a meaningful sequence — this re-sorts
 * client-side using the already-present grade_min/grade_max structured
 * data instead. */
export async function listPublicDivisions(seasonId) {
  const { data, error } = await supabase.rpc('list_public_divisions', { p_season_id: seasonId });
  if (error) throw error;
  return sortDivisions(data);
}

/** One Division's own header info (+ its Season/Program identity for
 * breadcrumbs) — supports deep-linking straight to a Division page. */
export async function getPublicDivision(divisionId) {
  const { data, error } = await supabase.rpc('get_public_division', { p_division_id: divisionId });
  if (error) throw error;
  return data?.[0] ?? null;
}

/** Every active Team in a Division, ALL of them regardless of
 * public_page_enabled (in-house Team names are shown on the Division
 * page itself) — `public_page_enabled` is included per-row so the
 * caller can decide whether to render a link or plain text. */
export async function listPublicDivisionTeams(divisionId) {
  const { data, error } = await supabase.rpc('list_public_division_teams', { p_division_id: divisionId });
  if (error) throw error;
  return data;
}

/** The single-Team public shell lookup — resolves to null unless the
 * Team has public_page_enabled = true, regardless of what the caller
 * already knows from listPublicDivisionTeams(); this is the real
 * enforcement point against dead/leaked Team links. */
export async function getPublicTeam(teamId) {
  const { data, error } = await supabase.rpc('get_public_team', { p_team_id: teamId });
  if (error) throw error;
  return data?.[0] ?? null;
}

/** Phase C.3 — every public Game for one Team, soonest first (041).
 * Empty (never an error) for a Team with no games, or one that isn't
 * actually public — the same "no row came back" convention every other
 * public RPC in this file already uses. The public Team page buckets
 * this one list into Schedule/Latest Results/record client-side (see
 * utils/gameResult.js) rather than calling a second RPC for each. */
export async function listPublicTeamGames(teamId) {
  const { data, error } = await supabase.rpc('list_public_team_games', { p_team_id: teamId });
  if (error) throw error;
  return data;
}

/** Phase C.3 — every public Game, across every public-enabled Team, for
 * one Program+Season pair (041). Powers the Program page's compact
 * Upcoming / Latest Results sections; a disabled Team's games are never
 * included, and a mismatched program/season pair always returns empty
 * rather than another Program's data. */
export async function listPublicProgramGames(programId, seasonId) {
  const { data, error } = await supabase.rpc('list_public_program_games', {
    p_program_id: programId,
    p_season_id: seasonId,
  });
  if (error) throw error;
  return data;
}

// ═══════════════════════════════════════════════════════════════════════
// Phase C.4 — internal Team-vs-Team matchups (team_matchups, 042).
// ═══════════════════════════════════════════════════════════════════════

/** Every public internal matchup involving this Team, oriented to its
 * own perspective — same shape as listPublicTeamGames() (opponent_name/
 * home_away/team_score/opponent_score), so PublicTeamPage.js merges both
 * arrays with no change to utils/gameResult.js's derivation functions.
 * Still requires this Team's OWN public_page_enabled = true — an
 * unpublished Team's direct public page stays fully protected. */
export async function listPublicTeamMatchups(teamId) {
  const { data, error } = await supabase.rpc('list_public_team_matchups', { p_team_id: teamId });
  if (error) throw error;
  return data;
}

/** Every internal matchup between two active Teams in this Division —
 * powers the public Division page's "Recent Results." Deliberately
 * INDEPENDENT of either Team's public_page_enabled (only Program public
 * visibility + Division status='active' + both Teams active are
 * required) — matching listPublicDivisionTeams()'s own existing
 * precedent of naming every Team in a Division regardless of that flag.
 * A Team's own direct public page remains fully gated separately. */
export async function listPublicDivisionGames(divisionId) {
  const { data, error } = await supabase.rpc('list_public_division_games', { p_division_id: divisionId });
  if (error) throw error;
  return data;
}

/** Every internal matchup across a Program+Season, one row per game —
 * merges into the Program page's Upcoming/Latest Results alongside
 * listPublicProgramGames(); duplicate-free by construction (queries
 * team_matchups directly, never a per-Team union). */
export async function listPublicProgramMatchups(programId, seasonId) {
  const { data, error } = await supabase.rpc('list_public_program_matchups', {
    p_program_id: programId,
    p_season_id: seasonId,
  });
  if (error) throw error;
  return data;
}
